import React, { useCallback, useContext, useEffect, useMemo } from 'react';
import { Tab, Tabs } from '@mui/material';
import routes from 'store/configs/Routes';
import { useHistory, useLocation } from 'react-router';
import TabPanel from '@mui/lab/TabPanel';
import TabContext from '@mui/lab/TabContext';
import Spinner from 'components/shared/Spinner';
import { UserContext } from 'components/UserGuard';
import MakeGroupPaymentPageView from 'components/payments/MakeGroupPaymentPageView';
import { getTabKeyByRoute, getTabRouteByKey } from 'util/Route';
import OrderHistoryPageView from 'components/payments/OrderHistoryPageView';
import PaymentService from 'services/api/PaymentService';
import { OrderPayment } from 'store/types/Payment';
import useRequest from 'hooks/useRequest';
import PaymentUser from 'store/types/PaymentUser';
import { getUserFullName } from 'util/Format';
import { ConfigContext } from 'components/ConfigGuard';
import SiteModule from 'store/enums/SiteModule';
import { getPageTitle } from 'util/Layout';
import ManageAccessSettingsPageView from 'components/payments/ManageAccessSettingsPageView';
import { ParametersContext } from 'components/ParametersGuard';
import { TabItem } from 'store/types/Tabs';
import { getDefaultTabRoute, getVisibleTabs } from 'util/TabHelper';

import commonStyles from 'styles/common.module.scss';

const tabConfig = {
  GroupPayments: { key: 'GroupPayments', label: 'Billing History', route: routes.groupPayments } as TabItem,
  MakeGroupPayment: { key: 'MakeGroupPayment', label: 'Pay Invoices', route: routes.makeGroupPayment } as TabItem,
  PaymentAccessSettings: {
    key: 'PaymentAccessSettings',
    label: 'Manage Access',
    route: routes.paymentAccessSettings,
  } as TabItem,
};

const GroupPaymentsPage: React.FunctionComponent = () => {
  const location = useLocation();
  const history = useHistory();
  const {
    groupPayments: { billingHistoryTabHeader },
  } = useContext(ParametersContext);
  const {
    isNccerTheme,
    sidebar,
    modulesSettings: {
      groupPayments: { hiddenTabs },
    },
  } = useContext(ConfigContext);
  const { userLoading = false } = useContext(UserContext);
  const ordersRequest = useRequest<OrderPayment[]>(PaymentService.getGroupPaymentOrdersList);
  const usersRequest = useRequest<PaymentUser[]>(PaymentService.getGroupPaymentUsers);
  const activeTab: string = useMemo(() => getTabKeyByRoute(location.pathname, tabConfig), [location.pathname]);
  const orderHistoryLoading: boolean = useMemo(
    () => ordersRequest.loading || usersRequest.loading,
    [ordersRequest.loading, usersRequest.loading]
  );

  const visibleTabs = useMemo(() => {
    return getVisibleTabs(tabConfig, hiddenTabs);
  }, [hiddenTabs]);

  const orderHistoryData: OrderPayment[] = useMemo(
    () =>
      ordersRequest.data && ordersRequest.data.length
        ? ordersRequest.data.map((order: OrderPayment) => {
            let result: OrderPayment = { ...order };

            if (order.ownerId) {
              const foundUser: PaymentUser | undefined = (usersRequest.data || []).find(
                ({ profileId }) => profileId === order.ownerId
              );

              if (foundUser) {
                result = { ...result, ownerName: getUserFullName(foundUser) };
              }
            }
            return result;
          })
        : [],
    [ordersRequest.data, usersRequest.data]
  );

  const handleTabChange = useCallback(
    (_: any, value = tabConfig.GroupPayments.key) => {
      history.replace(getTabRouteByKey(value, tabConfig));
    },
    [history]
  );

  useEffect(() => {
    history.replace(getDefaultTabRoute(location.pathname, visibleTabs));
  }, [history, location.pathname, visibleTabs]);

  return (
    <>
      <h1 className={commonStyles.pageTitle}>
        {getPageTitle(sidebar, SiteModule.GroupPayments) || 'Group Billing & Invoices'}
      </h1>
      <TabContext value={activeTab}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          indicatorColor={'primary'}
          textColor={'primary'}
          variant={'scrollable'}
        >
          {visibleTabs.map((tab) => {
            return (
              <Tab
                key={tab.key}
                value={tab.key}
                label={tab.label}
                className={commonStyles.tab}
                classes={{ selected: commonStyles.activeTab }}
              />
            );
          })}
        </Tabs>
        <TabPanel value={tabConfig.GroupPayments.key} className={commonStyles.tabPanel}>
          <Spinner loading={userLoading} transparent={false}>
            {billingHistoryTabHeader && <p dangerouslySetInnerHTML={{ __html: billingHistoryTabHeader }} />}
            <OrderHistoryPageView
              {...ordersRequest}
              loading={orderHistoryLoading}
              data={orderHistoryData}
              ownerColumnEnabled={true}
              companyColumnEnabled={isNccerTheme}
              openOrdersEnabled={true}
            />
          </Spinner>
        </TabPanel>
        <TabPanel value={tabConfig.MakeGroupPayment.key} className={commonStyles.tabPanel}>
          <Spinner loading={userLoading} transparent={false}>
            <MakeGroupPaymentPageView {...usersRequest} />
          </Spinner>
        </TabPanel>
        <TabPanel value={tabConfig.PaymentAccessSettings.key} className={commonStyles.tabPanel}>
          <Spinner loading={userLoading} transparent={false}>
            <ManageAccessSettingsPageView />
          </Spinner>
        </TabPanel>
      </TabContext>
    </>
  );
};

export default GroupPaymentsPage;
